// Showing prices in a visitor's currency: the currencies a site may offer, the European Central
// Bank's daily reference rates (euros per unit), and the conversion between them. Checkout still
// charges each price in the currency the creator set; these figures are for reading only.

export const displayCurrencies = ["usd", "eur", "gbp", "cad", "aud"] as const;
export type DisplayCurrency = (typeof displayCurrencies)[number];

export function isDisplayCurrency(value: unknown): value is DisplayCurrency {
  return typeof value === "string" && (displayCurrencies as readonly string[]).includes(value);
}

/** Units of each currency per euro, as the ECB publishes them, with the euro itself at 1. */
export type CurrencyRates = { date: string; perEuro: Record<DisplayCurrency, number> };

/** Reads the ECB's daily reference rates (eurofxref-daily.xml); null when one is missing. */
export function parseEcbRates(xml: string): CurrencyRates | null {
  const date = /time=['"](\d{4}-\d{2}-\d{2})['"]/.exec(xml)?.[1];
  if (!date) return null;
  const perEuro: Partial<Record<DisplayCurrency, number>> = { eur: 1 };
  for (const match of xml.matchAll(/currency=['"]([A-Z]{3})['"]\s+rate=['"]([\d.]+)['"]/g)) {
    const code = match[1]!.toLowerCase();
    const rate = Number(match[2]);
    if (isDisplayCurrency(code) && Number.isFinite(rate) && rate > 0) perEuro[code] = rate;
  }
  return displayCurrencies.every((code) => perEuro[code])
    ? { date, perEuro: perEuro as Record<DisplayCurrency, number> }
    : null;
}

/** An amount in another currency, rounded to the cent. */
export function convertCents(
  cents: number,
  from: DisplayCurrency,
  to: DisplayCurrency,
  rates: CurrencyRates,
) {
  if (from === to) return cents;
  return Math.round((cents / rates.perEuro[from]) * rates.perEuro[to]);
}

// How Intl's "en" formatting writes each currency (shop-format.ts `money`), longest first so
// "CA$" isn't read as "$". A symbol right after a letter is another currency's ("MX$", "NZ$",
// "R$"), whose amounts stay as written.
const symbols: readonly (readonly [string, DisplayCurrency])[] = [
  ["CA$", "cad"],
  ["A$", "aud"],
  ["US$", "usd"],
  ["$", "usd"],
  ["€", "eur"],
  ["£", "gbp"],
];
const amountPattern = /(?<![A-Za-z])(CA\$|A\$|US\$|\$|€|£)\s?(\d[\d,]*(?:\.\d{1,2})?)/g;

function format(cents: number, currency: DisplayCurrency) {
  return new Intl.NumberFormat("en", {
    style: "currency",
    currency: currency.toUpperCase(),
  }).format(cents / 100);
}

/**
 * The same words with every price ("$24.00", "From CA$12.50") written in another currency.
 * Text without a recognised price, and prices already in that currency, come back unchanged.
 */
export function convertPriceText(text: string, to: DisplayCurrency, rates: CurrencyRates) {
  return text.replace(amountPattern, (whole, symbol: string, amount: string) => {
    const from = symbols.find(([mark]) => mark === symbol)?.[1];
    const value = Number(amount.replaceAll(",", ""));
    if (!from || from === to || !Number.isFinite(value)) return whole;
    return format(convertCents(Math.round(value * 100), from, to, rates), to);
  });
}
